import React, { useState, useEffect, useContext } from 'react';
import { API, AuthContext } from '../../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import {
  MapPin, User, Building2, Mail, Phone, Clock,
  CheckCircle2, XCircle, RefreshCw, ExternalLink
} from 'lucide-react';

const STATUS_MAP = {
  bekliyor:   { label: 'Bekliyor',    bg: 'bg-amber-100 text-amber-700 border-amber-200' },
  onaylandı:  { label: 'Onaylandı',   bg: 'bg-green-100 text-green-700 border-green-200' },
  reddedildi: { label: 'Reddedildi',  bg: 'bg-red-100 text-red-600 border-red-200' },
};

export default function StandRequestsPanel() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [filter, setFilter] = useState('bekliyor');

  useEffect(() => { fetchRequests(); }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/expo/stand-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(res.data);
    } catch { toast.error('Talepler yüklenemedi'); }
    finally { setLoading(false); }
  };

  const handleAction = async (id, action) => {
    setActionLoading(id + action);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/expo/stand-requests/${id}`, { action }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(action === 'onayla' ? 'Talep onaylandı, stand rezerve edildi' : 'Talep reddedildi');
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    } finally { setActionLoading(null); }
  };

  const filtered = requests.filter(r => filter === 'all' || r.status === filter);
  const pendingCount = requests.filter(r => r.status === 'bekliyor').length;

  return (
    <div className="p-8" data-testid="stand-requests-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MapPin className="w-6 h-6 text-teal-600" /> Stand Talepleri
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Herkese açık fuar haritasından gelen stand talepleri
          </p>
        </div>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <span className="bg-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
              {pendingCount} bekliyor
            </span>
          )}
          <Button variant="outline" size="sm" onClick={fetchRequests} disabled={loading} className="gap-1.5 h-8">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Yenile
          </Button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {[
          { key: 'bekliyor', label: `Bekleyenler (${requests.filter(r => r.status === 'bekliyor').length})` },
          { key: 'onaylandı', label: `Onaylananlar (${requests.filter(r => r.status === 'onaylandı').length})` },
          { key: 'reddedildi', label: `Reddedilenler (${requests.filter(r => r.status === 'reddedildi').length})` },
          { key: 'all', label: `Tümü (${requests.length})` },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${filter === f.key ? 'border-teal-500 text-teal-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid={`filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="py-12 text-center text-sm text-gray-400">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-gray-300" /> Yükleniyor...
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center">
          <MapPin className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-400">Talep bulunamadı</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(req => {
            const statusInfo = STATUS_MAP[req.status] || STATUS_MAP.bekliyor;
            return (
              <div key={req.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-start gap-4" data-testid={`request-${req.id}`}>
                {/* Stand badge */}
                <div className="w-10 h-10 bg-teal-50 border border-teal-100 rounded-lg flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4 text-teal-600" />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="font-semibold text-gray-900">Stand {req.stand_number || '—'}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${statusInfo.bg}`}>
                      {statusInfo.label}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs text-gray-600">
                    <span className="flex items-center gap-1"><User className="w-3 h-3" /> {req.contact_name}</span>
                    <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {req.contact_company}</span>
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {req.contact_email}</span>
                    {req.contact_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {req.contact_phone}</span>}
                  </div>
                  {req.notes && <p className="text-xs text-gray-500 mt-1.5 bg-gray-50 rounded-md px-2 py-1">{req.notes}</p>}
                  <p className="text-[10px] text-gray-400 mt-1.5 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(req.created_at).toLocaleString('tr-TR')}
                  </p>
                </div>

                {/* Actions */}
                {req.status === 'bekliyor' && (
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <Button
                      size="sm"
                      onClick={() => handleAction(req.id, 'onayla')}
                      disabled={actionLoading === req.id + 'onayla'}
                      className="h-8 bg-green-600 hover:bg-green-700 gap-1.5 text-xs"
                      data-testid={`approve-${req.id}`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> Onayla
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAction(req.id, 'reddet')}
                      disabled={actionLoading === req.id + 'reddet'}
                      className="h-8 border-red-200 text-red-600 hover:bg-red-50 gap-1.5 text-xs"
                      data-testid={`reject-${req.id}`}
                    >
                      <XCircle className="w-3.5 h-3.5" /> Reddet
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
